// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps, ButtonVariants } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { color, ellipsis, xScreen } from "@plumberssupply/XDL/Utilities";
import React from "react";
import { css } from "styled-components";

const enum fields {
    image = "image",
    title = "title",
    description = "description",
    openInNewTab = "openInNewTab",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    buttonVariant = "buttonVariant"
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.title]: string;
        [fields.description]: string;
        [fields.openInNewTab]: boolean;
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonVariant]: ButtonVariants;
    };
}

type Props = OwnProps;

export interface InfoCardStyles {
    container?: InjectableCss;
    headingText?: TypographyPresentationProps;
    description?: TypographyProps;
    image?: LazyImageProps;
    textWrapper?: InjectableCss;
    buttonStyles?: ButtonPresentationProps;
}

export const infoCardStyles: InfoCardStyles = {
    container: {
        css: css`
            display: flex;
            height: 100%;
            background-color: ${color("common.white")};

            ${xScreen("lte", "md")} {
                flex-direction: column;
            }
        `,
    },
    textWrapper: {
        css: css`
            flex-grow: 1;
            max-width: 692px;
            padding: 32px 54px 32px 24px;

            ${xScreen("lte", "md")} {
                max-width: 100%;
            }
        `,
    },
    image: {
        width: "606px",
        height: "279px",
        css: css`
            img {
                width: 100%;
                height: 100%;
                object-fit: cover;
                object-position: center;
            }

            min-width: 300px;
            ${xScreen("lte", "md")} {
                flex-shrink: 1;
                width: 100%;
                height: 335px;
            }

            ${xScreen("lte", "xs")} {
                height: 152px;
                min-width: auto;
            }
        `,
    },
    headingText: {
        forwardAs: "h2",
        size: 28,
        weight: 800,
        color: "common.ui11",
        css: css`
            line-height: 34px;
            margin-bottom: 16px;
            ${ellipsis(2)}

            ${xScreen("lte", "xs")} {
                ${ellipsis(5)}
            }
        `,
    },
    description: {
        as: "p",
        color: "text.main",
        css: css`
            line-height: 26px;
            ${ellipsis(3)}

            ${xScreen("lte", "xs")} {
                ${ellipsis(8)}
            }
        `,
    },
    buttonStyles:{
        css: css`
            margin-top: 20px;
        `,
    }
};

const styles = infoCardStyles;

const InfoCard: React.FC<Props> = ({fields: {image, title, description, buttonLabel, buttonLink, buttonVariant, openInNewTab}}) => {

    const link = useGetLink(buttonLink)?.url;
    
    return (
        <StyledWrapper {...styles.container} data-test-selector='Ad-Widget'>
            <StyledWrapper {...styles.textWrapper}>
                <Typography className="ad-heading" {...styles.headingText}>
                    {title}
                </Typography>
                <Typography {...styles.description}>{description}</Typography>

                {(buttonLabel || link) &&
                    <Clickable href={link} target={openInNewTab ? "_blank" : undefined}>
                        <Button
                            className="ad-button"
                            sizeVariant="medium"
                            variant={buttonVariant}
                            {...styles.buttonStyles}
                        >
                            {buttonLabel || link}
                        </Button>
                    </Clickable>}

            </StyledWrapper>
            <LazyImage src={image} altText={title} {...styles.image} />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: InfoCard,
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isRequired: true,
            },
            {
                fieldType: "General",
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
            },
            {
                fieldType: "General",
                name: fields.description,
                displayName: "Description",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
            },
            {
                fieldType: "General",
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { value: "", type: "Page" }
            },
            {
                name: fields.buttonVariant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                fieldType: "General",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                hideEmptyOption: true,
                defaultValue: "primary",
            },
            {
                fieldType: "General",
                name: fields.openInNewTab,
                displayName: "Open in New Tab",
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
        ],
    },
};

export default widgetModule;
